// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import styles from '@features/voucher-new/styles/voucher-new.module.css';
import { VoucherInputField } from '@features/voucher-new/ui/components/VoucherInputField';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';

import { getFinalNumberFromDialCode, isRTL } from '@shared/utils/general';
import cn from 'classnames';
import { COUNTRY_SHORT_NAMES } from '@entities/country/model/constants';
import { getCountryPlaceholder } from '@entities/country/model/placeholders';
import { redeemVoucher } from '@features/voucher-subscription/model/voucher-subscribe-flow';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@shared/hooks';
import parsePhoneNumber from 'libphonenumber-js/mobile';
import { Helpers } from '@shared/utils';
import RoundLoader from '@shared/ui/round-loader/ui/RoundLoader';
import Alert from '@shared/ui/alert/ui/Alert';
import { detectCountry } from '@entities/country';
import { TelInput } from '@features/phone-subscription';
import { languageAtom, voucherAtom } from '@shared/store/slice-atoms';

export const VoucherNewForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { query } = useQuery();
  const isInternalDebugging = query.get('isInternalDebugging');

  const voucherCodeQuery = query.get('code') || null;

  const language = useAtomValue(languageAtom);
  const { btnLoading } = useAtomValue(voucherAtom);

  const { direction } = language;

  const isRTLDirection = isRTL(direction);

  const [isLoading, setIsLoading] = useState(true);

  const [email, setEmail] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(COUNTRY_SHORT_NAMES.AE);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_SHORT_NAMES.AE);

  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [voucherCodeError, setVoucherCodeError] = useState('');

  useEffect(() => {
    detectCountry()
      .then((response) => {
        const isoCode = response.data?.isoCode?.toLowerCase() || 'ae';
        setSelectedCountry(isoCode);
        setCountryCode(isoCode);
      })
      .catch(() => {
        setSelectedCountry('ae');
        setCountryCode('ae');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleEmailChange = (e) => {
    const text = e.target.value;
    setEmail(text);

    if (text.length > 0 && text.length < 255 && Helpers.isEmail(text)) {
      setEmailError('');
    } else {
      setEmailError(t('error_email'));
    }
  };

  const handleVoucherCodeChange = (e) => {
    const text = e.target.value;
    setVoucherCode(text);

    if (text.length > 0 && text.length < 255) {
      setVoucherCodeError('');
    } else {
      setVoucherCodeError(t('error_code'));
    }
  };

  const handlePhoneChange = (isValidd, inputAsIs, iso2, international) => {
    let lettersRegExp = /[a-zA-Z]/g; //:\'\".,-:;

    if (lettersRegExp.test(inputAsIs)) {
      return;
    }

    if (inputAsIs.includes('.') || inputAsIs.includes(',')) {
      return;
    }

    if (!iso2) return;
    if (iso2 && !iso2.iso2) return;
    const dialCode = iso2.dialCode;
    const isoCode = iso2?.iso2?.toUpperCase();
    setCountryCode(isoCode);
    let finalNumber = getFinalNumberFromDialCode(inputAsIs, dialCode);

    let dialCode2 = '972';

    if (dialCode === '970') {
      finalNumber = getFinalNumberFromDialCode(finalNumber, dialCode2);
    }

    const phoneNumber = parsePhoneNumber(finalNumber, isoCode);

    let newFinal = '';
    setPhone(inputAsIs);
    if (finalNumber.includes(`+${dialCode}`)) {
      newFinal = finalNumber.split(`+${dialCode}`)[1];
    } else if (finalNumber.includes(`+${dialCode2}`)) {
      newFinal = finalNumber.split(`+${dialCode2}`)[1];
    } else {
      newFinal = finalNumber;
    }
    setPhone(newFinal);

    if (phoneNumber && phoneNumber?.isValid()) {
      setPhoneError('');
    } else {
      setPhoneError(t('error_phone'));
    }
  };

  const handleSubmit = () => {
    let inputFields = {
      phone,
      email,
      countryCode,
      voucherCode,
      source: 'api-web-voucher',
      language: language.locale,
    };

    redeemVoucher(inputFields, history, isInternalDebugging);
  };

  useEffect(() => {
    let phoneTelInput = document.getElementById('phone-tel-input');
    if (phoneTelInput) {
      phoneTelInput.addEventListener('focusin', () => {
        document.getElementsByClassName('intl-tel-input')[0].classList.add('blue-border');
      });
      phoneTelInput.addEventListener('focusout', () => {
        document.getElementsByClassName('intl-tel-input')[0].classList.remove('blue-border');
      });

      phoneTelInput.addEventListener('blur', () => {
        document.getElementsByClassName('intl-tel-input')[0].classList.remove('blue-border');
      });
    }
  }, []);

  useEffect(() => {
    if (!voucherCodeQuery) return;

    setVoucherCode(voucherCodeQuery);
  }, [voucherCodeQuery]);

  const isFormValid =
    email && voucherCode && phone && !emailError && !phoneError && !voucherCodeError;

  const preferredCountries = useMemo(() => {
    return [selectedCountry, 'ae'];
  }, [isLoading]);

  if (isLoading) {
    return <RoundLoader />;
  }

  return (
    <div className={styles.voucherFormWrapper}>
      <VoucherInputField
        title={t('enter_your_email')}
        value={email}
        handleChange={handleEmailChange}
        placeholder={'example@domain'}
        error={emailError}
      />

      <div className={styles.voucherInputFieldWrapper}>
        <p className={cn(styles.inputFieldTitle, isRTLDirection ? styles.inputFieldTitleRTL : '')}>
          {t('enter_your_number')}
        </p>

        <TelInput
          hideCountryFlag={false}
          phone={phone}
          handlePhoneChange={handlePhoneChange}
          preferredCountries={preferredCountries}
          classname={styles.phoneInputClassname}
          setSelectedCountry={setSelectedCountry}
          placeholder={getCountryPlaceholder(selectedCountry, language.locale)}
        />

        {!!phoneError && <p className={styles.inputFieldError}>{phoneError}</p>}
      </div>

      {!Boolean(voucherCodeQuery) && (
        <VoucherInputField
          title={t('voucher_2')}
          value={voucherCode}
          handleChange={handleVoucherCodeChange}
          placeholder={t('enter_voucher_code')}
          error={voucherCodeError}
        />
      )}

      <Alert />

      <button
        className={cn(styles.voucherFormSubmit, isFormValid ? styles.voucherFormSubmitValid : '')}
        disabled={!isFormValid || btnLoading}
        onClick={handleSubmit}
      >
        {t('redeem')}
      </button>
    </div>
  );
};
